import { Link } from "react-router-dom";
import { Phone, Mail, Facebook, ShieldCheck, BadgeCheck, FlaskConical, Scale, MapPin, ArrowUpRight, ArrowRight } from "lucide-react";
import { NAV_LINKS, CONTACT_DETAILS } from "@/data/content";
import { LogoImage } from "@/components/Logo";

const badges = [
  { icon: BadgeCheck, label: "Enhanced DBS" },
  { icon: ShieldCheck, label: "Fully Insured" },
  { icon: FlaskConical, label: "COSHH Certified" },
];

const headingCls = "text-[11px] font-semibold text-champagne uppercase tracking-[0.22em] mb-5";
const linkCls = "inline-flex items-center min-h-[44px] lg:min-h-0 text-sm text-champagne/70 hover:text-rosegold transition-colors duration-150";

const Footer = () => {
  return (
    <footer data-testid="site-footer" className="relative bg-charcoal text-champagne/80">
      <div className="h-px w-full bg-rosegold-metallic" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <LogoImage
              sizes="112px"
              testId="footer-logo"
              className="h-28 w-28 shadow-lg shadow-black/40 ring-1 ring-rosegold/60 mb-5"
            />
            <p data-testid="footer-strapline" className="text-[11px] font-medium uppercase tracking-[0.22em] text-rosegold">
              Homes · Businesses · Happier Spaces
            </p>
            <p className="text-sm text-champagne/70 leading-relaxed mt-3">
              A small, family-run cleaning business. We take care of the cleaning, so you can take care of what matters.
            </p>
            <div className="flex flex-wrap gap-2 mt-5">
              {badges.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.14em] text-champagne/85 border border-rosegold/40 rounded-md px-2.5 py-1"
                >
                  <Icon className="w-3 h-3 text-rosegold" /> {label}
                </span>
              ))}
            </div>
          </div>

          <div>
            <h4 className={headingCls}>Quick Links</h4>
            <ul className="space-y-0 lg:space-y-2.5">
              {NAV_LINKS.map((link) => (
                <li key={link.id}>
                  <Link to={link.path} data-testid={`footer-link-${link.id}`} className={linkCls}>
                    {link.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/blog" data-testid="footer-link-blog" className={linkCls}>
                  Blog
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className={headingCls}>Get in touch</h4>
            <ul className="space-y-0 lg:space-y-3 text-sm text-champagne/80">
              <li className="flex items-center gap-2.5 min-h-[44px] lg:min-h-0">
                <Phone className="w-4 h-4 text-rosegold shrink-0" />
                <a
                  href={CONTACT_DETAILS.phoneHref}
                  data-testid="footer-phone"
                  className="inline-flex items-center min-h-[44px] lg:min-h-0 hover:text-rosegold transition-colors"
                >
                  {CONTACT_DETAILS.phone}
                </a>
              </li>
              <li className="flex items-center gap-2.5 min-h-[44px] lg:min-h-0">
                <Mail className="w-4 h-4 text-rosegold shrink-0" />
                <a
                  href={`mailto:${CONTACT_DETAILS.email}`}
                  data-testid="footer-email"
                  className="inline-flex items-center min-h-[44px] lg:min-h-0 hover:text-rosegold transition-colors break-all"
                >
                  {CONTACT_DETAILS.email}
                </a>
              </li>
              <li className="flex items-center gap-2.5 min-h-[44px] lg:min-h-0">
                <Facebook className="w-4 h-4 text-rosegold shrink-0" />
                <a
                  href={CONTACT_DETAILS.facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-facebook"
                  aria-label="Visit Five Point Cleaning on Facebook (opens in a new tab)"
                  className="group inline-flex items-center gap-1.5 min-h-[44px] lg:min-h-0 hover:text-rosegold transition-colors"
                >
                  <span className="underline decoration-rosegold/50 decoration-1 underline-offset-4 group-hover:decoration-rosegold">
                    {CONTACT_DETAILS.facebook}
                  </span>
                  <ArrowUpRight className="w-3.5 h-3.5 text-rosegold opacity-80 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </a>
              </li>
              <li className="flex items-center gap-2.5 min-h-[44px] lg:min-h-0">
                <MapPin className="w-4 h-4 text-rosegold shrink-0" />
                <span data-testid="footer-location">South Yorkshire & surrounding areas</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className={headingCls}>Free quote</h4>
            <p className="text-sm text-champagne/70 leading-relaxed mb-5">
              Send us your postcode, property details and the service you're looking for — we'll be in touch.
            </p>
            <Link to="/contact" data-testid="footer-quote-btn" className="btn btn-primary btn-sm">
              Request a Quote <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-champagne/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-champagne/50 text-center sm:text-left">
            © {new Date().getFullYear()} Five Point Cleaning. A family-run business. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link to="/terms" data-testid="footer-terms-link" className={`inline-flex items-center gap-1.5 min-h-[44px] text-xs font-medium ${linkCls}`}>
              <Scale className="w-3.5 h-3.5" />
              Terms & Conditions
            </Link>
            <span className="w-1 h-1 rounded-full bg-rosegold/50" aria-hidden="true" />
            <Link to="/terms#privacy" data-testid="footer-privacy-link" className={`inline-flex items-center gap-1.5 min-h-[44px] text-xs font-medium ${linkCls}`}>
              <ShieldCheck className="w-3.5 h-3.5" />
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
